import { useState } from 'react';

import { Button } from '@/components/ui';
import { Checkbox } from '@/components/ui/Checkbox';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/Input';
import { YStack } from '@/components/ui/layout/layout';
import { SearchInput } from '@/components/ui/SearchInput';
import type { HivemindGroup } from '@/features/hivemind/hivemind.types';

import { PersonCell } from '../components/person-cell';
import { filterPeople, hiveMindEmail, reachablePeople } from '../filter-people';

import { sameText, useSaveGroupMutation } from './groups.query';

import type { WikiPerson } from '../people-wiki.types';

/** Makes a group, or changes the name and the members of `group`. */
export const GroupDialog = ({
  group,
  groups,
  people,
  onClose,
}: {
  group: HivemindGroup | null;
  groups: HivemindGroup[];
  people: WikiPerson[];
  onClose: () => void;
}) => {
  const [name, setName] = useState(group?.name ?? '');
  const [query, setQuery] = useState('');
  const [emails, setEmails] = useState<string[]>(
    group?.members.map((member) => member.email) ?? []
  );
  const { mutate: save, isPending } = useSaveGroupMutation();
  const trimmed = name.trim();
  // The server refuses a name with a slash
  const hasSlash = trimmed.includes('/');
  // Saving under the name of another group would replace its members
  const isTaken = groups.some(
    (other) => other !== group && sameText(other.name, trimmed)
  );
  const candidates = filterPeople(reachablePeople(people), query);

  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open && !isPending) onClose();
      }}
    >
      <Dialog.Content>
        <Dialog.Header>
          <Dialog.Title>{group ? 'Edit group' : 'New group'}</Dialog.Title>
          <Dialog.Description>
            Name a group to message its people together.
          </Dialog.Description>
        </Dialog.Header>
        <YStack className="gap-3">
          <Input
            label="Group name"
            value={name}
            maxLength={64}
            error={
              hasSlash
                ? 'A group name cannot contain "/"'
                : isTaken
                  ? 'A group with this name exists'
                  : undefined
            }
            onChange={(event) => setName(event.target.value)}
            autoFocus
          />
          <SearchInput
            value={query}
            onChange={setQuery}
            placeholder="Search people..."
            aria-label="Search people for the group"
          />
          <YStack className="max-h-72 overflow-y-auto rounded-lg border border-border">
            {candidates.length === 0 ? (
              <span className="px-3 py-6 text-center text-sm text-foreground-muted">
                No people found
              </span>
            ) : (
              candidates.map((person) => {
                const email = hiveMindEmail(person);
                return (
                  email && (
                    <label
                      key={person.id}
                      className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-surface-hover"
                    >
                      <Checkbox
                        checked={emails.some((other) => sameText(other, email))}
                        onCheckedChange={(checked) =>
                          setEmails((current) =>
                            checked
                              ? [...current, email]
                              : current.filter(
                                  (other) => !sameText(other, email)
                                )
                          )
                        }
                      />
                      <PersonCell person={person} email={email} />
                    </label>
                  )
                );
              })
            )}
          </YStack>
        </YStack>
        <Dialog.Footer>
          <Button variant="secondary" disabled={isPending} onClick={onClose}>
            Cancel
          </Button>
          <Button
            isLoading={isPending}
            disabled={
              trimmed === '' || hasSlash || isTaken || emails.length === 0
            }
            onClick={() =>
              save(
                {
                  name: trimmed,
                  memberEmails: emails,
                  previousName: group?.name ?? null,
                },
                { onSuccess: onClose }
              )
            }
          >
            Save
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};
